import { LicenseName } from "@/data/license.data";
import { Country } from "@/data/country";
import { signIn, useSession } from "next-auth/react";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";
import { useEffect } from "react";
import { getUserLicense } from "@/actions/db/get-user-license";
import { isUserSubscriptionExpired } from "@/services/paypro-payment/paypro-payment-provider";
import { getCountry } from "@/actions/utils/country";
import { PendingConfigStorageMixin } from "@/services/storage/storage-mixins/pending-config-storage-mixin";
import { AuthorizedProgressContextState } from "@/hooks/progress-context/use-service-progress-provider";

type UserData = {
  userId: string;
  licenseName: LicenseName;
  country: Country | undefined;
};

type ContactUsErrorData = {
  attachedFile?: File;
  message?: string;
  subject?: string;
};

const createFormData = (
  additionalFormFill: (formData: FormData) => void,
  userData: UserData,
) => {
  const formData = new FormData();
  formData.append("userId", userData.userId);
  formData.append("licenseName", userData.licenseName);
  if (userData.country) {
    formData.append("country", userData.country);
  }

  additionalFormFill(formData);
  return formData;
};

const handleErrorResponse = (error: string, showErrorModal: () => void) => {
  switch (error) {
    case "auth":
      signIn();
      return;
    case "internal_error":
      showErrorModal();
      return;
    default:
      showErrorModal();
      return;
  }
};

// TODO: make base extends type for here and in pending-config-storage-mixin
export const useStartProcess = <
  TConfig extends { songFile?: File | null; text?: string },
  TProgressContext extends AuthorizedProgressContextState,
>(
  startProcessConfigCreator: (config: TConfig) => {
    createConfigFormData: (formData: FormData) => void;
  },
  pendingConfigStorage: PendingConfigStorageMixin<TConfig>,
  startApiEndpoint: string,
  progressContext: TProgressContext,
  showLimitedModal: (licenseName: LicenseName) => void,
) => {
  const { data, status } = useSession();

  const { showModal } = useGlobalModalContext();

  const showSubscriptionExpiredModal = (licenseName: LicenseName) =>
    showModal({
      type: "subscription_expired_modal",
      props: { licenseName: licenseName, shouldNavigateToPricing: true },
    });

  const showErrorModal = (modalData: {
    title?: string;
    message?: string;
    errorData?: ContactUsErrorData;
  }) =>
    showModal({
      type: "error_modal",
      props: {
        title: modalData.title,
        message: modalData.message,
        feedbackData: modalData.errorData,
      },
    });

  useEffect(() => {
    const getConfig = async () => {
      if (data?.user?.id) {
        const storedConfig = await pendingConfigStorage.getPendingConfigData();
        if (storedConfig) {
          await pendingConfigStorage.setPendingConfigData(null);
          await startAiProcess(storedConfig);
        }
      }
    };

    getConfig();
  }, [data?.user?.id]);

  const startAiProcess = async (
    processConfig: TConfig,
    errorData?: ContactUsErrorData,
  ) => {
    const userId = data?.user?.id;
    const startProcessConfig = startProcessConfigCreator(processConfig);
    if (!userId) {
      await pendingConfigStorage.setPendingConfigData(processConfig);
      signIn();
      return;
    }

    const userLicense = (await getUserLicense(userId)) as LicenseName;

    // temp added
    // подивитись далі по роботі PayPro, чи не виникне ситуацій, коли в юзера підписка закінчилась, а ми якимсь чином не змогли оновити йому ліцензію
    const isSubscriptionExpired = await isUserSubscriptionExpired(userId);
    if (userLicense !== "Trial" && isSubscriptionExpired) {
      showSubscriptionExpiredModal(userLicense);
      return;
    }

    if (!processConfig.songFile && !processConfig.text) {
      return;
    }

    const userCountry = await getCountry();

    const userData = {
      userId: userId,
      licenseName: userLicense,
      country: userCountry,
    };

    const formData = createFormData(
      startProcessConfig.createConfigFormData,
      userData,
    );

    const response = await fetch(startApiEndpoint, {
      method: "POST",
      body: formData,
    });

    if (response.status === 413) {
      showErrorModal({
        title: "Song file is too large",
        message: "Please upload a smaller file",
      });
      return;
    }
    if (response.status === 500) {
      showErrorModal({
        title: "Server error",
        message: "Conversion server doesn't respond.",
      });
      progressContext.resetContext();
      return;
    }

    progressContext.setPercent(0);
    progressContext.setStatus("preparing");

    const processResponse = await response.json();
    if (processResponse["type"] === "error") {
      progressContext.setPercent(0);
      progressContext.setStatus("");
      if (processResponse["error"] === "license_limit_expired") {
        showLimitedModal(userLicense);
        return;
      }
      handleErrorResponse(processResponse, () =>
        showErrorModal({ errorData: errorData }),
      );
      return;
    }
    const processId = processResponse["id"] as string;
    progressContext.setProcessId(processId);
  };

  return { startAiProcess };
};
